/**
 * AiTutorWidget — scoped Q&A tutor for an Agentic AI Mastery module (Path 2B).
 *
 * A floating action button anchors to the corner of a module page. Clicking it
 * opens a side Drawer with a chat-style transcript, an input box, and a send
 * button. Each assistant reply carries thumbs up/down feedback; replies that the
 * backend refused as assessment questions render with a distinct banner.
 *
 * Rendered only on unlocked modules (the parent gates this).
 *
 * Conversation is lazily created on first send — no network call on open.
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import SendIcon from "@mui/icons-material/Send";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import {
  createTutorConversation,
  sendTutorMessage,
  setTutorThumbs,
} from "../api/client";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

interface AiTutorWidgetProps {
  slug: string;
  moduleTitle?: string;
}

interface ChatMessage {
  // Local id for keying; assistant rows also carry the server message id for thumbs.
  localId: string;
  serverId?: string;
  role: "user" | "assistant";
  content: string;
  refused?: boolean;
  thumbs?: "up" | "down" | null;
}

let _idSeq = 0;
function nextId(): string {
  _idSeq += 1;
  return `m${_idSeq}`;
}

export default function AiTutorWidget({ slug, moduleTitle }: AiTutorWidgetProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages, sending]);

  async function handleSend() {
    const question = draft.trim();
    if (!question || sending) return;

    setError(null);
    setDraft("");
    const userMsg: ChatMessage = { localId: nextId(), role: "user", content: question };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);

    try {
      let convId = conversationId;
      if (!convId) {
        const conv = await createTutorConversation(slug);
        convId = conv.id;
        setConversationId(convId);
      }

      const resp = await sendTutorMessage(convId, question);
      setMessages((prev) => [
        ...prev,
        {
          localId: nextId(),
          serverId: resp.message_id,
          role: "assistant",
          content: resp.content,
          refused: resp.was_assessment_refused,
          thumbs: null,
        },
      ]);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 429) {
        setError(t("AiTutorWidget.hourlyLimit"));
      } else {
        setError(t("AiTutorWidget.respondError"));
      }
    } finally {
      setSending(false);
    }
  }

  async function handleThumbs(localId: string, value: "up" | "down") {
    const msg = messages.find((m) => m.localId === localId);
    if (!msg?.serverId) return;
    // Toggle off if clicking the same value again.
    const next = msg.thumbs === value ? null : value;
    setMessages((prev) =>
      prev.map((m) => (m.localId === localId ? { ...m, thumbs: next } : m))
    );
    if (next) {
      try {
        await setTutorThumbs(msg.serverId, next);
      } catch {
        // Non-critical — leave the optimistic state; feedback is best-effort.
      }
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <>
      <Tooltip title={t("AiTutorWidget.askTooltip")}>
        <Fab
          color="primary"
          aria-label={t("AiTutorWidget.askTooltip")}
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            bgcolor: theme.palette.primary.main,
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            zIndex: (t) => t.zIndex.drawer - 1,
          }}
        >
          <SchoolIcon />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 420 }, display: "flex", flexDirection: "column" },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <SchoolIcon sx={{ color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={500} lineHeight={1.1}>
                {t("AiTutorWidget.title")}
              </Typography>
              {moduleTitle && (
                <Typography variant="caption" color="text.secondary">
                  {moduleTitle}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton aria-label={t("AiTutorWidget.closeTutor")} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Message list */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {messages.length === 0 && !sending && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t("AiTutorWidget.intro")}
            </Typography>
          )}

          <Stack spacing={1.5}>
            {messages.map((m) => (
              <MessageBubble key={m.localId} message={m} onThumbs={handleThumbs} />
            ))}

            {sending && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
                <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
                <Typography variant="caption" color="text.secondary">
                  {t("AiTutorWidget.thinking")}
                </Typography>
              </Stack>
            )}
            <div ref={listEndRef} />
          </Stack>

          {error && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        {/* Input */}
        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder={t("AiTutorWidget.questionPlaceholder")}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              inputProps={{ "aria-label": t("AiTutorWidget.questionAriaLabel") }}
            />
            <IconButton
              aria-label={t("AiTutorWidget.send")}
              onClick={handleSend}
              disabled={sending || !draft.trim()}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.common.white,
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                "&.Mui-disabled": { bgcolor: alpha(theme.palette.primary.main, 0.3), color: theme.palette.common.white },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}

// ---------------------------------------------------------------------------
// Single message bubble
// ---------------------------------------------------------------------------

interface MessageBubbleProps {
  message: ChatMessage;
  onThumbs: (localId: string, value: "up" | "down") => void;
}

function MessageBubble({ message, onThumbs }: MessageBubbleProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <Box sx={{ alignSelf: "flex-end", maxWidth: "85%" }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 2,
            ml: "auto",
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {message.content}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ alignSelf: "flex-start", maxWidth: "90%" }}>
      {message.refused && (
        <Alert
          icon={<WarningAmberIcon fontSize="inherit" />}
          severity="info"
          sx={{ mb: 0.5, borderRadius: 2, py: 0 }}
        >
          {t("AiTutorWidget.refused")}
        </Alert>
      )}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {message.content}
        </Typography>
      </Paper>

      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {t("AiTutorWidget.wasThisHelpful")}
        </Typography>
        <IconButton
          size="small"
          aria-label={t("AiTutorWidget.thumbsUp")}
          onClick={() => onThumbs(message.localId, "up")}
        >
          {message.thumbs === "up" ? (
            <ThumbUpAltIcon fontSize="inherit" sx={{ color: theme.palette.primary.main }} />
          ) : (
            <ThumbUpOffAltIcon fontSize="inherit" />
          )}
        </IconButton>
        <IconButton
          size="small"
          aria-label={t("AiTutorWidget.thumbsDown")}
          onClick={() => onThumbs(message.localId, "down")}
        >
          {message.thumbs === "down" ? (
            <ThumbDownAltIcon fontSize="inherit" sx={{ color: theme.palette.primary.main }} />
          ) : (
            <ThumbDownOffAltIcon fontSize="inherit" />
          )}
        </IconButton>
      </Stack>
    </Box>
  );
}
